/* eslint-disable react/state-in-constructor */
/* eslint-disable react/sort-comp */
import {
	BodyText,
	Flex,
	IconV2,
	Select,
} from '@bamboohr/fabric';
import { toCamelCase } from 'BambooHR.util';
import { isEnabled } from 'FeatureToggle.util';
import { Component, Fragment, createRef } from 'react';

import './state-withholding.styl';
import { AdditionalWithholding } from '../additional-withholding.react';
import { StateWHMigrationMessage } from '../state-wh-migration-message.react/state-wh-migration-message';
import { LinkedSelects } from '../linked-selects.react';
import { OptionalTextInput } from '../optional-text-input.react';
import { OptionalCheckbox } from '../optional-checkbox.react';
import { OptionalSelect } from '../optional-select.react';
import { OptionalUsdInput } from '../optional-usd-input/optional-usd-input';
import { SectionStyledBox } from '../section-styled-box';
import { SectionStyledHeader } from '../section-styled-header';
import { numberWithinRange } from '../../utils';
import { getShouldMigrateToNewForm } from '../withholding.react/withholding.domain';
import { WithholdingSubsection } from './withholding-subsection';

const feAccessLevelsQuickForms = isEnabled('PAY_INFO_TAB_ACCESS_LEVELS');

import c from 'classnames';

const OPTIONAL_FIELDS_MAP = {
	text: OptionalTextInput,
	checkbox: OptionalCheckbox,
	select: OptionalSelect,
	linkedSelects: LinkedSelects,
	usd: OptionalUsdInput,
};

/**
 * @extends {Component<import("./types").StateWithholdingProps, import("./types").StateWithholdingComponentState>}
 */
export class StateWithholding extends Component {
	/**
	 * @param {import("./types").StateWithholdingProps} props 
	 */
	constructor(props) {
		super(props);
		this._helpPopoverRef = createRef();
		this._locationSelectRef = createRef();
		this._statusSelectRef = createRef();
		/** @type {string | null} */
		let filingStatus = null;
		let exemptions = 0;
		let ignore = false;
		/** @type {string | null} */
		let stateAbbreviation = null;
		const selectedLocation = props.selectedLocation === undefined || props.selectedLocation === null ? props.locations.selected : props.selectedLocation;
		const activeLocation = selectedLocation ? props.locations.byId[selectedLocation] : null;

		if (activeLocation) {
			const selectedFilingStatus = activeLocation.fields.filingStatus ? activeLocation.fields.filingStatus.find(status => status.selected === true) : null;
			filingStatus = selectedFilingStatus ? selectedFilingStatus.val : null;
			exemptions = activeLocation.fields.exemptions ? activeLocation.fields.exemptions.amount : 0;
			ignore = activeLocation.fields.ignoreStatusExemptions ? activeLocation.fields.ignoreStatusExemptions.checked : false;
			stateAbbreviation = activeLocation.state;
		}

		/**
		 * @type {import("./types").StateWithholdingProps}
		 */
		this.state = {
			shouldMigrateToNewForm: props.shouldMigrateToNewForm,
			selectedLocation,
			filingStatus,
			exemptions,
			ignore,
			stateAbbreviation,
			showHelpPopover: false,
		};
	}

	/**
	 * Should we disable fields because you should migrate to the new form
	 * 
	 * Typically this will be the same as `shouldMigrateToNewForm` but will be `false` if we're in greenhouse.
	 */
	get disableFieldsBecauseYouShouldMigrateToNewForm() {
		const { isGreenhouse } = this.props
		const { shouldMigrateToNewForm } = this.state

		return isGreenhouse ? false : shouldMigrateToNewForm
	}

	render() {
		const { 
			selectedLocation, 
			shouldMigrateToNewForm, 
			stateAbbreviation,
		} = this.state;
		const { disableFieldsBecauseYouShouldMigrateToNewForm } = this
		const {
			accessLevel,
			editPermission,
			handleFormMigrationClick,
			headerHeadlineSize,
			isAdminUser,
			isPayrollAdminUser,
			locations,
			sectionNote,
			showWithholding,
			usesSectionComponent,
			viewOnly,
		} = this.props;
		const activeLocation = locations.byId[selectedLocation];

		/** 
		 * Optional fields are fields relevant to specific States, and they are treated as required by BE.
		 * This variable name is a misnomer and generates confusion, but we use it because what it's called
		 * in a dependent system.
		 */
		let optionalFields = null;
		
		/** Collection of optional field components to be rendered as subsections. */
		const optionalFieldSubsection = [];

		if (activeLocation && activeLocation.fields.optionalFields) {

			// Tags the first optional fields checkbox as the first checkbox so that
			optionalFields = activeLocation.fields.optionalFields.allIds.reduce((hasCheckbox, id) => {
				const field = activeLocation.fields.optionalFields.byId[id];
				if (field.type === 'checkbox') {
					field.data.firstCheckbox = !hasCheckbox;
					hasCheckbox = true;
				}
				return hasCheckbox;
			}, false);

			// dynamically configure and render the optional fields as form components
			optionalFields = activeLocation.fields.optionalFields.allIds.map((id) => {
				const field = activeLocation.fields.optionalFields.byId[id];
				const { label } = field.data;

				if(!isEnabled('PAYROLL_INDIANA_COUNTY_TAX_OPTIONS')) {
					// We'll keep this optional field hidden until the work for Indiana's County of Residence Phase 1 is complete.
					// TODO (GB-2973): remove after Indiana County of Residence Phase 1 release.
					if (label === 'County of Primary Residence') {
						return null;
					}

					// We'll preserve the `Principal Employment` label until the PAYROLL_INDIANA_COUNTY_TAX_OPTIONS feature toggle is enabled.
					// TODO (GB-2996): remove after Indiana County of Residence Phase 1 release.
					if (label === 'County of Principal Employment') {
						field.data.label = $.__('Principal Employment');
					}
				}

				// NOTE: If location is Iowa, we want to hide these particular optional fields from this view.
				if (activeLocation.state === 'IA' &&
					(label === 'Married Filing Jointly with Both Spouses Working' ||
						label === 'Total Allowances')) {
					return null;
				}
				
				// map the field type to the appropriate component
				const FieldComponent = OPTIONAL_FIELDS_MAP[field.type];

				const biId = `stateTaxWithholding-${stateAbbreviation}-${toCamelCase(label)}-${id}`;

				const renderedFieldComponent = <div className="fieldDiv" key={ id }>
					<FieldComponent
						biId={ biId }
						data={ field.data }
						help={ field.help }
						id={ id }
						required={ field.required }
						viewOnly={ viewOnly || disableFieldsBecauseYouShouldMigrateToNewForm }
					/>
				</div>

				// Render Indiana County of Residence fields as its own group
				if(isEnabled('PAYROLL_INDIANA_COUNTY_TAX_OPTIONS')) {
					// We've decided to make these checks using labels for now. If/when we have a need a pattern for
					// creating subsections, BE may consider including these as option groups.
					if (label === 'County of Primary Residence' || label === 'County of Principal Employment') {
						optionalFieldSubsection.push(
							renderedFieldComponent
						);
						return null;
					}
				}
				

				return (
					renderedFieldComponent
				);
			});

		}

		const formMigrationMessage = shouldMigrateToNewForm ? <StateWHMigrationMessage
			onMigrateFormClick={ handleFormMigrationClick }
			stateAbbreviation={stateAbbreviation}
		/> : null;

		return <SectionStyledBox
			hidden={ showWithholding === false }
			usesSectionComponent={ usesSectionComponent }
		>
			<fieldset>
				<SectionStyledHeader
					hasPopover={ true }
					headlineSize={ headerHeadlineSize }
					popoverContent={ $.__('This determines how much you will withhold from your paycheck for state taxes.') }
					popoverIcon="circle-question-regular"
					popoverPlacement="bottom"
					popoverTitle={ $.__('State Tax Withholding') }
				>
					{ $.__('State Tax Withholding') }
				</SectionStyledHeader>
				{ sectionNote ? (
					<>
						<BodyText
							color="info-medium"
							icon={<IconV2
								color="info-medium"
								name="circle-info-solid"
								size={ 16 }
							/>}
							size='small'
							weight="semibold"
						>
							{ sectionNote }
						</BodyText>
					</>
				) : null }

				{ formMigrationMessage }
				<Flex flexDirection="column" gap={2} marginTop={2}>

					{ locations && this._renderLocationsSelect() }

					{ activeLocation && (
						<Fragment>
							{activeLocation.fields?.filingStatus || activeLocation.fields?.exemptions ? (
								<Flex gap={1}>
									{ activeLocation.fields.filingStatus && this._renderFilingStatusSelect(activeLocation.fields.filingStatus) }
									{ activeLocation.fields.exemptions && this._renderExemptionsInput(activeLocation.fields.exemptions) }
								</Flex>
							) : null}
							{ feAccessLevelsQuickForms && accessLevel !== undefined ? (
									<>
										{ ((editPermission || viewOnly || disableFieldsBecauseYouShouldMigrateToNewForm) && activeLocation.fields.ignoreStatusExemptions) && this._renderIgnoreCheckbox(activeLocation.fields) }
									</>
								) : (
									<>
										{ ((isAdminUser || isPayrollAdminUser || editPermission) && activeLocation.fields.ignoreStatusExemptions) && this._renderIgnoreCheckbox(activeLocation.fields) }
									</>
								) }
							{ activeLocation.fields.additionalWithholding && <AdditionalWithholding data={ activeLocation.fields.additionalWithholding } type="state" viewOnly={ viewOnly || disableFieldsBecauseYouShouldMigrateToNewForm } /> }
							{ optionalFields }
						</Fragment>
					) }
				</Flex>
				
				{optionalFieldSubsection.length ? <WithholdingSubsection
					formFields={optionalFieldSubsection}
					stateAbbr={stateAbbreviation}
				/> : null}
			</fieldset>
		</SectionStyledBox>
	}


	/**
	 * Get data object to be passed to location Select component
	 * @return {object}
	 */
	_getLocationSelectItems = () => {
		const {
			locations,
		} = this.props;
		const options = locations.allIds.map((location) => {
			const option = locations.byId[location];
			option.id = location;
			option.value = option.val;
			option.text = option.val;
			return option;
		});

		return options;
	};


	/**
	 * Get data object to be passed to filing status Select component
	 * @return {object}
	 */
	_getStatusSelectData = (filingStatus) => {
		const {
			ignore,
		} = this.state;

		const options = filingStatus.map((status) => {
			return {
				text: ignore ? 'NA' : status.displayText,
				value: status.val,
			};
		});

		return options;
	};

	// Event handlers

	_handleLocationChange = (selectedValues) => {
		const {
			locations,
			onLocationChange,
			setSelectedStateLocation,
		} = this.props;
		if (this._locationSelectRef.current) {
			this._locationSelectRef.current.classList.remove('error');
		}
		const [selectedValue] = selectedValues;
		const selectedLocation = locations.byId[selectedValue];
		this.setState({
			selectedLocation: selectedLocation ? selectedLocation.id : null,
			stateAbbreviation: selectedLocation ? selectedLocation.state : null,
			shouldMigrateToNewForm: getShouldMigrateToNewForm(locations, selectedLocation?.id),
		});
		setSelectedStateLocation(selectedValue);
		if (onLocationChange) {
			onLocationChange({
				val: selectedValue,
			});
		}
	};


	_handleStatusChange = (selectedValues) => {
		if (this._statusSelectRef.current) {
			this._statusSelectRef.current.classList.remove('error');
		}
		const [selectedValue = ''] = selectedValues;
		this.setState({
			filingStatus: selectedValue,
		});
	};

	_handleExemptionsChange = (event, range) => {
		if (numberWithinRange(event.target.value, range.min, range.max)) {
			this.setState({
				exemptions: event.target.value
			});
		}
	};

	_handleIgnoreChange = event => this.setState({
		ignore: event.target.checked
	});

	_getLabelClass = () => {
		const { viewOnly } = this.props;
		const { disableFieldsBecauseYouShouldMigrateToNewForm } = this;

		return c({ 'fab-Label': true }, { 'fab-Label--disabled': viewOnly || disableFieldsBecauseYouShouldMigrateToNewForm });
	};

	// Render different fields
	_renderLocationsSelect = () => {
		const {
			newHirePacket,
			showLocationRequiredAsterisk,
			viewOnly,
		} = this.props;
		const {
			selectedLocation,
			stateAbbreviation,
		} = this.state;
		const { disableFieldsBecauseYouShouldMigrateToNewForm } = this;

		const noteClass = 'fab-FormNote';

		return <div className="fieldBox js-stateLocationFieldBox required">
			<label className={
				c({
					'fab-Label': true,
					'fab-Label--disabled': viewOnly && !disableFieldsBecauseYouShouldMigrateToNewForm,
					// Making the required asterisk opt-in for now as there might be pre-existing usages that wouldn't want it to show?
					'fab-Label--required': !!showLocationRequiredAsterisk
				})
			} htmlFor="stateLocation">{ $.__('Location') }</label>
			<div className="fieldDiv">
				<Select
					id="stateLocation"
					isDisabled={ (viewOnly && !disableFieldsBecauseYouShouldMigrateToNewForm) || newHirePacket }
					items={ this._getLocationSelectItems() }
					name="state[location]"
					notClearable={ true }
					onChange={ this._handleLocationChange }
					required={ showLocationRequiredAsterisk }
					selectedValues={ [selectedLocation] }
					width={ 5 }
				/>
			</div>
			{ stateAbbreviation && (
				<span className={ noteClass }>
					{ $.__('State: %1$s', stateAbbreviation) }
				</span>
			) }
		</div>
	};

	_renderFilingStatusSelect = (filingStatus) => {
		const {
			viewOnly,
		} = this.props;
		const {
			filingStatus: selectedFilingStatus,
			ignore,
		} = this.state;
		const { disableFieldsBecauseYouShouldMigrateToNewForm } = this;
		const fieldBoxClasses = c(
			'fieldBox js-stateStatusFieldBox',
			{ 'required': !ignore }
		);

		return (
            (<div className={ fieldBoxClasses } ref={ this._statusSelectRef }>
                <label className={
					c(this._getLabelClass(), {
						'fab-Label--required': true
					})
				} htmlFor="stateStatus">{ $.__('Filing Status') }</label>
                <div className="fieldDiv">
					<Select
                        id="stateStatus"
                        isDisabled={ ignore || viewOnly || disableFieldsBecauseYouShouldMigrateToNewForm }
                        items={ this._getStatusSelectData(filingStatus) }
                        name="state[status]"
                        notClearable={ false }
                        onChange={ this._handleStatusChange }
                        placeholder={ ignore ? 'NA' : '-- Select --' }
                        selectedValues={ [selectedFilingStatus] }
                        width={ 8 }
                    />
				</div>
            </div>)
        );
	};

	_renderExemptionsInput = (exemptions) => {
		const inputClasses = 'fab-TextInput fab-TextInput--width3';

		return (
			<div className="fieldBox">
				<label className={ this._getLabelClass() } htmlFor="stateExemptions">{ $.__('Exemptions') }</label>
				<div className="fieldDiv">
					<input
						className={ inputClasses }
						disabled={ this.state.ignore || this.props.viewOnly || this.disableFieldsBecauseYouShouldMigrateToNewForm }
						id="stateExemptions"
						name="state[dependents]"
						onChange={ event => this._handleExemptionsChange(event, exemptions.range) }
						type="text"
						value={ this.state.ignore ? 'NA' : this.state.exemptions }
					/>
				</div>
			</div>
		);
	};

	_renderIgnoreCheckbox = () => {
		const labelText = $.__('Ignore filing status/other adjustments');

		return (
			<div className="fieldBox">
				<div className="fieldDiv">
					<div className="fab-Checkbox StateWithholding__exempt">
						<input
							className="fab-Checkbox__input"
							defaultChecked={ this.state.ignore ? 'checked' : null }
							disabled={ this.props.viewOnly || this.disableFieldsBecauseYouShouldMigrateToNewForm }
							id="stateIgnore"
							name="state[taxExempt]"
							onChange={ this._handleIgnoreChange }
							type="checkbox"
						/>
						<span className="fab-Checkbox__box"></span>
						<label className="fab-Checkbox__label" htmlFor="stateIgnore">{ labelText }</label>
					</div>
				</div>
			</div>
		);
	};
}
